import { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import api, { errMsg } from "@/lib/api";
import { CheckCircle2, XCircle, ExternalLink, Info } from "lucide-react";

// Provider metadata → dictates which form fields render + external doc link.
const PROVIDER_META = {
    meta: {
        label: "Meta (Facebook + Instagram)",
        desc: "Publish posts to Facebook Page and Instagram Business account.",
        docs: "https://developers.facebook.com/docs/instagram-api",
        fields: [
            { key: "app_id", label: "App ID", secret: false },
            { key: "app_secret", label: "App Secret", secret: true },
            { key: "page_id", label: "Facebook Page ID", secret: false },
            { key: "ig_business_id", label: "Instagram Business ID", secret: false },
            { key: "webhook_verify_token", label: "Webhook verify token", secret: true },
        ],
    },
    gmb: {
        label: "Google Business Profile",
        desc: "Publish posts + read reviews on Google Business Profile.",
        docs: "https://developers.google.com/my-business",
        fields: [
            { key: "client_id", label: "OAuth Client ID", secret: false },
            { key: "client_secret", label: "OAuth Client Secret", secret: true },
            { key: "location_id", label: "GBP Location ID (locations/…)", secret: false },
        ],
    },
    cashfree: {
        label: "Cashfree Payments",
        desc: "Live payment gateway. When Live mode is off, MockGateway is used.",
        docs: "https://www.cashfree.com/docs/payments/online/api-integration",
        fields: [
            { key: "client_id", label: "Cashfree App ID", secret: false },
            { key: "client_secret", label: "Cashfree Secret Key", secret: true },
            { key: "environment", label: "Environment (SANDBOX | PRODUCTION)", secret: false },
        ],
    },
    ai: {
        label: "AI / LLM",
        desc: "Emergent universal key is used by default. Override here to point at a specific provider.",
        docs: "",
        fields: [
            { key: "provider", label: "Provider (emergent | anthropic | openai)", secret: false },
            { key: "model", label: "Model alias", secret: false },
            { key: "api_key", label: "API key (leave empty to use Emergent)", secret: true },
        ],
    },
    google_indexing: {
        label: "Google Fast-Indexing",
        desc: "Auto-submit doctor URLs via Google Indexing API + IndexNow on publish.",
        docs: "https://developers.google.com/search/apis/indexing-api/v3/quickstart",
        fields: [
            { key: "service_account_email", label: "Service account email (read-only, from .env)", secret: false },
            { key: "search_console_property", label: "Search Console property URL", secret: false },
        ],
        customTest: true,
    },
};

export default function SuperAdminIntegrations() {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState({});
    const [seo, setSeo] = useState(null);
    const [seoForm, setSeoForm] = useState({ meta: "", filename: "", content: "" });
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);

    async function load() {
        try {
            const r = await api.get("/super-admin/integrations/");
            setRows(r.data.integrations || []);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load integrations"));
        }
        try {
            const s = await api.get("/super-admin/seo/");
            setSeo(s.data);
            setSeoForm({
                meta: s.data.gsc_verification_meta_tag || "",
                filename: s.data.gsc_verification_filename || "",
                content: "",
            });
        } catch (e) { /* noop */ }
    }
    useEffect(() => { load(); }, []);

    async function saveSeo() {
        try {
            const r = await api.put("/super-admin/seo/", {
                gsc_verification_meta_tag: seoForm.meta.trim() || null,
                gsc_verification_filename: seoForm.filename.trim() || null,
                gsc_verification_content: seoForm.content.trim() || null,
            });
            toast.success(r.data.file_written ? "Verification file saved + served at root" : "Meta tag saved");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Save failed");
        }
    }

    async function bulkSubmit() {
        if (!window.confirm("Submit ALL published doctors to Google Indexing API + IndexNow?")) return;
        setBulkBusy(true);
        try {
            const r = await api.post("/super-admin/indexing/bulk-submit");
            setBulkResult(r.data);
            toast.success(`Submitted ${r.data.total} URLs · Google OK ${r.data.google_ok} · IndexNow OK ${r.data.indexnow_ok}`);
        } catch (e) {
            toast.error(errMsg(e, "Bulk submit failed"));
        } finally { setBulkBusy(false); }
    }

    return (
        <AdminShell>
            <div className="mb-6" data-testid="super-integrations-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Integrations</h1>
                <p className="text-[#17212B]/60 text-sm mt-1">
                    Credentials for Meta, Google, Cashfree and AI. Secrets are stored server-side and shown masked.
                    All providers default to <strong>Demo mode</strong> until you set credentials and toggle Live.
                </p>
            </div>
            <div className="space-y-4">
                {rows.map((row) => (
                    <IntegrationCard key={row.provider} row={row} busy={busy} setBusy={setBusy} reload={load} />
                ))}

                {/* --- Resend (Email) — env-driven, dedicated card --- */}
                <ResendCard />

                {/* --- Search Console verification (self-service) --- */}
                <Card className="border-[#123B5D]/10" data-testid="seo-gsc-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-[#123B5D] flex items-center gap-2">
                            Search Console verification
                            {seo?.gsc_verification_file_exists ? (
                                <Badge className="bg-[#15803D] text-white" data-testid="seo-file-live">File live</Badge>
                            ) : (
                                <Badge variant="outline" className="border-[#D97706] text-[#D97706]">Not verified</Badge>
                            )}
                            {seo?.gsc_verification_meta_tag ? (
                                <Badge className="bg-[#0F766E] text-white">Meta tag live</Badge>
                            ) : null}
                        </CardTitle>
                        <p className="text-xs text-[#17212B]/60 mt-1">
                            Search Console → Add property → <strong>URL prefix</strong> (NOT Domain) → paste your PUBLIC_BASE_URL → pick HTML tag OR HTML file method → fill either box below → Save → click Verify. No redeploy needed.
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <Label className="text-xs">HTML tag method — meta content value only</Label>
                            <Input value={seoForm.meta} onChange={(e) => setSeoForm({ ...seoForm, meta: e.target.value })} placeholder="e.g. FPZ_J4C70WANpRQZ3efoDB2EZ3tK4chGxfvc73BKElo" data-testid="seo-meta-input" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">HTML file method — filename</Label>
                                <Input value={seoForm.filename} onChange={(e) => setSeoForm({ ...seoForm, filename: e.target.value })} placeholder="googleXXXXXXXX.html" data-testid="seo-filename-input" />
                            </div>
                            <div>
                                <Label className="text-xs">HTML file method — content</Label>
                                <Input value={seoForm.content} onChange={(e) => setSeoForm({ ...seoForm, content: e.target.value })} placeholder="google-site-verification: ..." data-testid="seo-content-input" />
                            </div>
                        </div>
                        {seo?.gsc_verification_url && (
                            <a href={seo.gsc_verification_url} target="_blank" rel="noreferrer" className="text-xs text-[#2563EB] font-semibold inline-flex items-center gap-1" data-testid="seo-verify-link">
                                {seo.gsc_verification_url} <ExternalLink size={11} />
                            </a>
                        )}
                        <div className="flex gap-2">
                            <Button size="sm" onClick={saveSeo} data-testid="seo-save">Save & serve at root</Button>
                        </div>
                    </CardContent>
                </Card>

                {/* --- Bulk submit --- */}
                <Card className="border-[#2563EB]/20" data-testid="bulk-submit-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-[#123B5D]">Submit ALL published doctors now</CardTitle>
                        <p className="text-xs text-[#17212B]/60 mt-1">
                            Fires Google Indexing API (URL_UPDATED) + IndexNow for every published /dr/&lt;slug&gt; plus the landing and /doctors pages. Run this once <strong>after</strong> Search Console verification succeeds and after adding the service account as OWNER of the property.
                        </p>
                    </CardHeader>
                    <CardContent>
                        <Button onClick={bulkSubmit} disabled={bulkBusy} data-testid="bulk-submit-btn">
                            {bulkBusy ? "Submitting…" : "Submit all now"}
                        </Button>
                        {bulkResult && (
                            <div className="mt-4 text-xs text-[#17212B]/80" data-testid="bulk-submit-result">
                                <div>Total URLs: <strong>{bulkResult.total}</strong> · Google OK: <strong>{bulkResult.google_ok}</strong> · IndexNow OK: <strong>{bulkResult.indexnow_ok}</strong></div>
                                <div className="text-[#17212B]/50 mt-1">Honest note: Google typically takes hours-to-days to actually index — never guaranteed.</div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminShell>
    );
}

function IntegrationCard({ row, busy, setBusy, reload }) {
    const meta = PROVIDER_META[row.provider] || { label: row.provider, fields: [], docs: "" };
    const [mode, setMode] = useState(row.mode || "demo");
    const [creds, setCreds] = useState({}); // only fields the user changes are sent
    // For google_indexing, the SA is in .env — hydrate the read-only email
    // from the /indexing/status endpoint so the card actually shows it.
    const [saEmail, setSaEmail] = useState("");
    useEffect(() => {
        if (row.provider !== "google_indexing") return;
        api.get("/super-admin/indexing/status").then((r) => setSaEmail(r.data?.client_email || "")).catch(() => {});
    }, [row.provider]);

    const val = (k) => {
        if (row.provider === "google_indexing" && k === "service_account_email") {
            return creds[k] !== undefined ? creds[k] : (row.credentials?.[k] ?? saEmail);
        }
        return creds[k] !== undefined ? creds[k] : (row.credentials?.[k] ?? "");
    };
    // google_indexing uses env-based credentials (service account JSON) — treat
    // the presence of a loaded client_email as "credentials present" so we
    // don't show the red "Live mode requires credentials" warning.
    const hasEnvCreds = row.provider === "google_indexing" && !!saEmail;
    const hasAnyCred = hasEnvCreds || Object.values(row.credentials || {}).some(Boolean);

    async function save() {
        setBusy((b) => ({ ...b, [row.provider]: true }));
        try {
            // Merge current (masked) creds + user edits — but only send edits + non-secret fields.
            const outCreds = { ...(row.credentials || {}), ...creds };
            // Strip masked (starts with '•') so we don't overwrite real secrets with dots.
            for (const k of Object.keys(outCreds)) {
                if (typeof outCreds[k] === "string" && outCreds[k].startsWith("•")) delete outCreds[k];
            }
            await api.put(`/super-admin/integrations/${row.provider}`, { mode, credentials: outCreds });
            toast.success(`${meta.label} saved (${mode})`);
            setCreds({});
            reload();
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setBusy((b) => ({ ...b, [row.provider]: false }));
        }
    }

    async function testConnection() {
        setBusy((b) => ({ ...b, [`${row.provider}_test`]: true }));
        try {
            // google_indexing uses a different endpoint that returns a rich response
            const url = row.provider === "google_indexing"
                ? "/super-admin/indexing/test"
                : `/super-admin/integrations/${row.provider}/test`;
            const r = await api.post(url, {});
            if (row.provider === "google_indexing") {
                // Custom rich toast for the indexing card
                const g = r.data?.google;
                const inow = r.data?.indexnow;
                const parts = [];
                if (g?.ok) parts.push("Google Indexing OK");
                else parts.push(`Google Indexing ${g?.status_code || "ERR"}: ${g?.response?.error?.message || "denied"}`);
                if (inow?.ok) parts.push(`IndexNow ${inow.status_code} OK`);
                else parts.push(`IndexNow: ${inow?.error || "not sent"}`);
                if (g?.status_code === 403) {
                    toast.warning(parts.join(" · "), {
                        description: "Google Indexing needs Search Console domain verification + service account added as OWNER. See setup guide.",
                        duration: 9000,
                    });
                } else {
                    toast.success(parts.join(" · "), { duration: 6000 });
                }
                return;
            }
            if (r.data.ok) {
                toast.success(r.data.message || "Test OK", {
                    description: r.data.dev_mode_hint || undefined,
                    duration: 7000,
                });
            } else {
                toast.warning(r.data.message || "Test not ready", { duration: 6000 });
            }
        } catch (e) {
            toast.error(errMsg(e, "Test failed"));
        } finally {
            setBusy((b) => ({ ...b, [`${row.provider}_test`]: false }));
        }
    }

    return (
        <Card className="border-[#123B5D]/10" data-testid={`integration-card-${row.provider}`}>
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <CardTitle className="text-[#123B5D] flex items-center gap-2">
                            {meta.label}
                            {mode === "live" ? (
                                <Badge className="bg-[#15803D] text-white" data-testid={`badge-live-${row.provider}`}>Live</Badge>
                            ) : (
                                <Badge variant="outline" className="border-[#D97706] text-[#D97706]" data-testid={`badge-demo-${row.provider}`}>Demo</Badge>
                            )}
                            {row.provider === "google_indexing" && !hasEnvCreds && (
                                <Badge variant="outline" className="border-[#123B5D]/40 text-[#123B5D]/70 text-[10px]">Setup pending</Badge>
                            )}
                        </CardTitle>
                        <p className="text-xs text-[#17212B]/60 mt-1">{meta.desc}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Label className="text-xs">Live</Label>
                        <Switch
                            checked={mode === "live"}
                            onCheckedChange={(v) => setMode(v ? "live" : "demo")}
                            data-testid={`toggle-mode-${row.provider}`}
                        />
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {meta.fields.length === 0 ? (
                    <div className="text-sm text-[#17212B]/60">No configurable fields.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {meta.fields.map((f) => (
                            <div key={f.key}>
                                <Label className="text-xs">{f.label}{f.secret ? " (secret)" : ""}</Label>
                                <Input
                                    type={f.secret ? "password" : "text"}
                                    value={val(f.key)}
                                    placeholder={f.secret && row.credentials?.[f.key] ? "(unchanged)" : ""}
                                    onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
                                    data-testid={`field-${row.provider}-${f.key}`}
                                />
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-center gap-2 pt-2 flex-wrap">
                    <Button size="sm" onClick={save} disabled={busy[row.provider]} data-testid={`save-${row.provider}`}>
                        {busy[row.provider] ? "Saving…" : "Save"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={testConnection}
                            disabled={busy[`${row.provider}_test`]} data-testid={`test-${row.provider}`}>
                        {busy[`${row.provider}_test`] ? "Testing…" : "Test connection"}
                    </Button>
                    {meta.docs ? (
                        <a href={meta.docs} target="_blank" rel="noreferrer"
                           className="text-xs text-[#0F766E] font-semibold inline-flex items-center gap-1 ml-2">
                            Setup guide <ExternalLink size={11} />
                        </a>
                    ) : null}
                    {mode === "live" && !hasAnyCred && (
                        <span className="text-xs text-[#DC2626] inline-flex items-center gap-1 ml-auto">
                            <XCircle size={12} /> Live mode requires credentials
                        </span>
                    )}
                    {mode === "live" && hasEnvCreds && (
                        <span className="text-xs text-[#15803D] inline-flex items-center gap-1 ml-auto">
                            <CheckCircle2 size={12} /> Service account loaded from .env
                        </span>
                    )}
                    {mode === "demo" && (
                        <span className="text-xs text-[#17212B]/60 inline-flex items-center gap-1 ml-auto">
                            <Info size={12} /> Demo returns simulated data — safe for testing
                        </span>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}


function ResendCard() {
    const [status, setStatus] = useState(null);
    const [to, setTo] = useState("");
    const [sending, setSending] = useState(false);
    const [lastSend, setLastSend] = useState(null);

    async function load() {
        try {
            const r = await api.get("/super-admin/integrations/resend/status");
            setStatus(r.data);
            if (!to && r.data?.last_from) setTo("");
        } catch (e) {
            // silent — card just shows N/A
        }
    }
    useEffect(() => { load(); }, []);

    async function sendTest() {
        setSending(true); setLastSend(null);
        try {
            const r = await api.post("/super-admin/integrations/resend/test", to ? { to } : {});
            setLastSend(r.data);
            if (r.data.ok) {
                toast.success(`Sent via ${r.data.from} · id ${(r.data.message_id || "").slice(0, 8)}`);
            } else {
                toast.error(r.data.error || "Send failed");
            }
            await load();
        } catch (e) {
            toast.error(errMsg(e, "Test send failed"));
        } finally {
            setSending(false);
        }
    }

    const verified = status?.domain_verified;
    return (
        <Card className="border-[#0F766E]/20" data-testid="resend-card">
            <CardHeader className="pb-3">
                <CardTitle className="text-[#123B5D] flex items-center gap-2">
                    Resend — Transactional email
                    {status?.provider === "resend" && status?.api_key_present ? (
                        <Badge className="bg-[#15803D] text-white" data-testid="resend-key-badge">Key set</Badge>
                    ) : (
                        <Badge variant="outline" className="border-[#D97706] text-[#D97706]">Not configured</Badge>
                    )}
                    {verified === true ? (
                        <Badge className="bg-[#15803D] text-white" data-testid="resend-domain-verified">Domain verified</Badge>
                    ) : verified === false ? (
                        <Badge variant="outline" className="border-[#D97706] text-[#D97706]" data-testid="resend-domain-unverified">Domain not verified — using fallback</Badge>
                    ) : (
                        <Badge variant="outline">Unknown</Badge>
                    )}
                </CardTitle>
                <p className="text-xs text-[#17212B]/60 mt-1">
                    Powers OTP, welcome, payment and publish emails. If <code>{status?.from || "no-reply@doctorsprofile.in"}</code> isn&apos;t verified in Resend, sends transparently fall back to <code>{status?.from_fallback || "onboarding@resend.dev"}</code>.
                </p>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-xs text-[#17212B]/80">
                    <div>API key: <code>{status?.api_key_masked || "—"}</code></div>
                    <div>OTP-only mode: <strong>{status?.otp_only_mode ? "ON" : "OFF (dev display active)"}</strong></div>
                    <div>Successful sends: <strong>{status?.sends_ok ?? 0}</strong></div>
                    <div>Failed sends: <strong>{status?.sends_failed ?? 0}</strong></div>
                    <div className="col-span-2">Last message id: <code>{status?.last_message_id || "—"}</code></div>
                </div>
                {verified === false && (
                    <div className="rounded-md bg-[#FEF3C7] border border-[#D97706]/40 p-3 text-xs text-[#78350F]" data-testid="resend-verify-help">
                        <strong>Verify your domain to remove the fallback:</strong>
                        <ol className="list-decimal ml-4 mt-1 space-y-1">
                            <li>Open <a className="underline text-[#123B5D]" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a> and add <code>doctorsprofile.in</code>.</li>
                            <li>Copy the SPF, DKIM and DMARC records shown.</li>
                            <li>In Hostinger DNS, add each record exactly as shown (type/host/value).</li>
                            <li>Return to Resend and click <em>Verify</em> — usually propagates in 5–30 min.</li>
                        </ol>
                    </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Optional — defaults to Resend account owner" data-testid="resend-test-to" />
                    <Button size="sm" onClick={sendTest} disabled={sending} data-testid="resend-test-btn" className="whitespace-nowrap">
                        {sending ? "Sending…" : "Send test email"}
                    </Button>
                </div>
                {lastSend && (
                    <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded-md p-2 overflow-auto max-h-40" data-testid="resend-test-result">
                        {JSON.stringify(lastSend, null, 2)}
                    </pre>
                )}
            </CardContent>
        </Card>
    );
}
